// Extração por regras da ficha catalográfica (CIP) a partir do texto do arquivo no Drive.
// Código puro, sem dependências do Deno, compartilhado com o app (modo demonstração e testes).

export interface ExtractedCatalogRecord {
  transcription: string
  cdd: string
  cdu: string
  cataloger: string
  subjects: string[]
}

export type ExtractedCatalogRecordField = keyof ExtractedCatalogRecord

/** Id de um arquivo do Drive a partir dos formatos de link mais comuns (Docs, Drive e `?id=`). */
export function extractDriveFileId(url: string) {
  const value = url.trim()
  if (!value) return null
  const pathMatch = value.match(/\/(?:document|file|spreadsheets|presentation)\/(?:u\/\d+\/)?d\/([A-Za-z0-9_-]{10,})/)
  if (pathMatch) return pathMatch[1]
  const queryMatch = value.match(/[?&]id=([A-Za-z0-9_-]{10,})/)
  if (queryMatch) return queryMatch[1]
  return /^[A-Za-z0-9_-]{20,}$/.test(value) ? value : null
}

const xmlEntities: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'" }

function decodeXmlEntities(value: string) {
  return value.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, entity: string) => {
    if (entity[0] !== "#") return xmlEntities[entity.toLowerCase()] ?? ""
    const code = entity[1].toLowerCase() === "x" ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10)
    return Number.isFinite(code) ? String.fromCodePoint(code) : ""
  })
}

/** Texto corrido do `word/document.xml` de um .docx: um parágrafo por linha, tabulações e quebras preservadas. */
export function docxDocumentXmlToText(xml: string) {
  const body = xml
    .replace(/<w:tab\/>/g, "\t")
    .replace(/<w:(?:br|cr)\b[^>]*\/>/g, "\n")
    .replace(/<\/w:p>/g, "\n")
    .replace(/<w:tc>/g, "\t")
    .replace(/<[^>]+>/g, "")
  return decodeXmlEntities(body)
}

const subjectsHeaderPattern = /^[íi]ndices? para cat[áa]logo sistem[áa]tico/i
const catalogerPattern = /\bCRB\b|bibliotec[áa]ri[oa]/i
const classificationLinePattern = /^(?:\d{2}-\d{3,}\s+)?(?:CDD|CDU)\b/i

function normalizeLines(text: string) {
  return text
    .replace(/\r\n?/g, "\n")
    .replace(/[   ]/g, " ")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
}

function uniqueJoin(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].join("; ")
}

function extractClassification(text: string, label: "CDD" | "CDU") {
  const pattern = new RegExp(`\\b${label}\\s*[-–:]?\\s*([^\\n]+)`, "gi")
  const values: string[] = []
  for (const match of text.matchAll(pattern)) {
    // Para na próxima classificação da mesma linha ("CDD 242 CDU 27") ou em espaços largos de tabela.
    const value = match[1].split(/\s{2,}|\t|\b(?:CDD|CDU)\b/i)[0].replace(/[.;,]+$/, "").trim()
    if (/\d/.test(value)) values.push(value)
  }
  return uniqueJoin(values)
}

function cleanCataloger(line: string) {
  return line
    .replace(/^.*?(?:respons[áa]vel|elaborad[ao]\s+(?:por|pel[ao])|catalogad[ao]\s+por)\s*[:\-–—]?\s*/i, "")
    .replace(/\s*[-–—,]?\s*bibliotec[áa]ri[oa]\s*(?=[-–—,]|$)/i, "")
    .replace(/\s*[-–—,]\s*(?=CRB)/i, " – ")
    .replace(/^[-–—:\s]+|[.\s]+$/g, "")
    .trim()
}

function extractSubjects(lines: string[]) {
  const start = lines.findIndex((line) => subjectsHeaderPattern.test(line))
  if (start < 0) return []
  const subjects: string[] = []
  const inlineRest = lines[start].replace(subjectsHeaderPattern, "").replace(/^[:\s]+/, "")
  const sectionLines = inlineRest ? [inlineRest, ...lines.slice(start + 1)] : lines.slice(start + 1)
  for (const line of sectionLines) {
    if (!line) {
      if (subjects.length) break
      continue
    }
    if (catalogerPattern.test(line) || classificationLinePattern.test(line)) break
    const entry = line.match(/^\d+\s*[.)-]\s*(.+)$/)
    if (entry) subjects.push(entry[1].trim())
    else if (subjects.length) subjects[subjects.length - 1] = `${subjects[subjects.length - 1]} ${line}`
    else subjects.push(line)
  }
  return subjects
}

/** Texto completo da ficha, como no arquivo: sem espaços sobrando e sem linhas em branco repetidas. */
function extractTranscription(lines: string[]) {
  const paragraphs: string[] = []
  let previousBlank = true
  for (const line of lines) {
    if (!line) {
      if (!previousBlank) paragraphs.push("")
      previousBlank = true
      continue
    }
    paragraphs.push(line)
    previousBlank = false
  }
  return paragraphs.join("\n").trim()
}

/** Reconhece as partes de uma ficha no padrão CIP (CBL ou bibliotecário independente). Campos não encontrados ficam vazios. */
export function parseCatalogRecordText(text: string): ExtractedCatalogRecord {
  const lines = normalizeLines(text)
  const normalized = lines.join("\n")
  const catalogerLine = lines.find((line) => /\bCRB\b/i.test(line)) ?? lines.find((line) => catalogerPattern.test(line))
  return {
    transcription: extractTranscription(lines),
    cdd: extractClassification(normalized, "CDD"),
    cdu: extractClassification(normalized, "CDU"),
    cataloger: catalogerLine ? cleanCataloger(catalogerLine) : "",
    subjects: extractSubjects(lines),
  }
}

export function extractedCatalogRecordFields(record: ExtractedCatalogRecord): ExtractedCatalogRecordField[] {
  return (Object.keys(record) as ExtractedCatalogRecordField[]).filter((field) => {
    const value = record[field]
    return Array.isArray(value) ? value.length > 0 : Boolean(value)
  })
}

export const SUPPLEMENTARY_FILES_FOLDER_NAME = "Arquivos complementares"

/** Reconhece a pasta "Arquivos complementares" com variações de caixa, acentos e numeração ("05. ARQUIVOS COMPLEMENTARES"). */
export function isSupplementaryFilesFolderName(name: string) {
  const normalized = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/^\s*\d+\s*[.\-_)]*\s*/, "")
    .replace(/[\s_-]+/g, " ")
    .trim()
  return normalized === "arquivos complementares"
}

export function isDocxFileName(name: string) {
  return /\.docx$/i.test(name.trim())
}

export const CATALOG_RECORD_FILE_TITLE_LENGTH = 24

/** Arquivos que o Farol guarda em "Arquivos complementares" da pasta do item. */
export type SupplementaryFileKind = "CATALOG_RECORD" | "CREDITS" | "FAO" | "BARCODE"

export const SUPPLEMENTARY_FILE_KINDS: SupplementaryFileKind[] = ["CATALOG_RECORD", "CREDITS", "FAO", "BARCODE"]

export const DOCX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
export const SVG_MIME_TYPE = "image/svg+xml"

/** Prefixo do nome, extensão e formato de cada arquivo: os documentos são enviados em Word; o código de barras é gerado em SVG. */
export const SUPPLEMENTARY_FILE_TYPES: Record<SupplementaryFileKind, { prefix: string; extension: string; mimeType: string }> = {
  CATALOG_RECORD: { prefix: "FICHA CATALOGRÁFICA", extension: ".docx", mimeType: DOCX_MIME_TYPE },
  CREDITS: { prefix: "CREDITOS", extension: ".docx", mimeType: DOCX_MIME_TYPE },
  FAO: { prefix: "FAO", extension: ".docx", mimeType: DOCX_MIME_TYPE },
  BARCODE: { prefix: "CODIGO DE BARRAS", extension: ".svg", mimeType: SVG_MIME_TYPE },
}

/** Nome fixo no Drive: prefixo do tipo ("FICHA CATALOGRÁFICA", "CREDITOS", "FAO", "CODIGO DE BARRAS") + " - " + os primeiros 24 caracteres do nome do item, sem reticências. */
export function supplementaryFileName(kind: SupplementaryFileKind, itemName: string) {
  const title = Array.from(itemName.replace(/[\\/]/g, "_").replace(/\s+/g, " ").trim()).slice(0, CATALOG_RECORD_FILE_TITLE_LENGTH).join("").trim()
  const { prefix, extension } = SUPPLEMENTARY_FILE_TYPES[kind]
  return `${prefix} - ${title || "Sem título"}${extension}`
}

/** O arquivo enviado tem a extensão do tipo (.docx nos documentos, .svg no código de barras). */
export function hasSupplementaryFileExtension(kind: SupplementaryFileKind, name: string) {
  return name.trim().toLowerCase().endsWith(SUPPLEMENTARY_FILE_TYPES[kind].extension)
}

/** Onde o item guarda o link de cada arquivo complementar, como vem de `get_catalog_item`. */
export interface SupplementaryFilesBook {
  creditsFileUrl?: string
  barcodeFileUrl?: string
  catalogRecord?: { driveFileUrl?: string } | null
  fao?: { driveFileUrl?: string } | null
}

export function savedSupplementaryFileUrl(book: SupplementaryFilesBook | null | undefined, kind: SupplementaryFileKind) {
  if (kind === "CREDITS") return book?.creditsFileUrl
  if (kind === "FAO") return book?.fao?.driveFileUrl
  if (kind === "BARCODE") return book?.barcodeFileUrl
  return book?.catalogRecord?.driveFileUrl
}

/** Ids dos arquivos salvos no item para os outros tipos: nunca podem ser regravados nem apagados por este tipo. */
export function otherSupplementaryFileIds(book: SupplementaryFilesBook | null | undefined, kind: SupplementaryFileKind) {
  return new Set(SUPPLEMENTARY_FILE_KINDS.filter((other) => other !== kind).map((other) => extractDriveFileId(savedSupplementaryFileUrl(book, other) ?? "")).filter((id): id is string => Boolean(id)))
}

export function catalogRecordFileName(itemName: string) {
  return supplementaryFileName("CATALOG_RECORD", itemName)
}
